import * as React from 'react';
import { useMemo } from 'react';
import { Drawer, useMediaQuery, useTheme } from '@mui/material';
import type { Breakpoint } from '@mui/material/styles';
import { useAppSelector } from '../../../services/redux/hooks';
import { makeGetTotalHeightForContexts } from '../../../services/redux/dimensionsSlice';

export const SIDEBAR_WIDTH = 240;

type Anchor = 'top' | 'left' | 'bottom' | 'right';

interface TemporaryDrawerProps {
  anchor: Anchor;
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode; // Add children prop to accept any React elements
  pinnedFrom?: Breakpoint; // when the viewport is at least this MUI breakpoint, render as a permanent (pinned) drawer instead of a temporary overlay
  width?: number; // pinned drawer width in px
}

export default function TemporaryDrawer({ anchor, isOpen, onClose, children, pinnedFrom, width = SIDEBAR_WIDTH }: TemporaryDrawerProps) {
  const theme = useTheme();
  const isPinned = useMediaQuery(pinnedFrom ? theme.breakpoints.up(pinnedFrom) : '(min-width:100000px)');
  const getTotalHeight = useMemo(makeGetTotalHeightForContexts, []);
  const headerHeight = useAppSelector(state => (pinnedFrom ? getTotalHeight(state, ['chrome'], ['header', 'appbar']) : 0));
  const footerHeight = useAppSelector(state => (pinnedFrom ? getTotalHeight(state, ['chrome'], ['footer']) : 0));

  if (isPinned) {
    return (
      <Drawer
        variant="permanent"
        anchor={anchor}
        open
        sx={{
          width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            top: `${headerHeight}px`,
            bottom: `${footerHeight}px`,
            height: 'auto',
            overflow: 'auto',
          },
        }}
      >
        {children}
      </Drawer>
    );
  }

  return (
    <div>
      <Drawer
        anchor={anchor}
        open={isOpen}
        onClose={onClose}
      >
        {children}
      </Drawer>
    </div>
  );
}
